import React, { useMemo, useState } from "react"
import { Column } from "@tanstack/react-table"
import { Eye, MoveDownRight, MoveUpRight } from "lucide-react"

import { DataTable } from "components/data-table/data-table"
import { DataTableToolbar } from "components/data-table/data-table-toolbar"
import { DataTableColumnHeader } from "components/data-table/data-table-column-header"

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"

import { useDataTable } from "@/hooks/shadcn/useDataTable"
import { cn } from "@/utils/shadcn-utils"
import { getLocalStringDateTime } from "@/utils/date"
import { formatMoney, getFilePath } from "@/utils/utils"
import { green, red } from "@/themes/colors"

import type { TransactionType } from "@/types/TransactionsType"
import { CodePromoType } from "@/types/CodePromoType"

/* ---------------- UTILS ---------------- */

const getStatusStyle = (status: string) => {
  switch (status) {
    case "Succès":
      return "bg-green-100 text-green-600"
    case "Echec":
      return "bg-red-100 text-red-500"
    default:
      return "bg-gray-100 text-gray-500"
  }
}

const getLocalStringDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};


function TransactionDataTable() {
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionType | null>(null)

  const showDetails = (transaction: TransactionType) => {
    setSelectedTransaction(transaction)
    setIsDetailOpen(true)
  }


  const columns = useMemo(
    () => [
      {
        id: "id",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }: any) => (
          <div className="flex items-center gap-2">
            {row.original.type.toLowerCase().includes("débit") ? (
              <MoveUpRight
                color={red["500"]}
                className="h-5 w-5 rounded-full bg-red-100 p-1"
              />
            ) : (
              <MoveDownRight
                color={green["500"]}
                className="h-5 w-5 rounded-full bg-green-100 p-1"
              />
            )}
            <span>TR-00{row.original.id}</span>
          </div>
        ),
        size: 100,
      },

      {
        accessorKey: "date",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Date" />
        ),
        cell: ({ row }: any) =>
          getLocalStringDateTime(row.original.date),
        size: 150,
      },

      {
        accessorKey: "client",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Client" />
        ),
        size: 130,
      },

      {
        accessorKey: "type",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Type" />
        ),
        size: 200,
      },

      {
        accessorKey: "amount",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Montant" />
        ),
        cell: ({ row }: any) => (
          <span
            className={cn(
              "font-medium",
              row.original.type.toLowerCase().includes("débit") &&
              "text-danger"
            )}
          >
            {row.original.type.toLowerCase().includes("débit") ? "- " : "+ "}
            {formatMoney(Math.abs(row.original.amount))} Fcfa
          </span>
        ),
        size: 180,
      },

      {
        accessorKey: "status",
        header: ({ column }: any) => (
          <DataTableColumnHeader column={column} title="Statut" />
        ),
        cell: ({ row }: any) => (
          <span
            className={cn(
              "px-3 py-1 rounded-lg text-xs font-medium",
              getStatusStyle(row.original.status)
            )}
          >
            {row.original.status}
          </span>
        ),
        size: 100,
      },

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: any) => (
          <Eye
            size={22}
            className="cursor-pointer ml-3"
            onClick={() => showDetails(row.original)}
          />
        ),
        size: 80,
      },
    ],
    []
  )


  const transactionsData = useMemo<TransactionType[]>(
    () => [
      {
        id: 1,
        amount: 2000000,
        type: "Crédit - Recharge",
        solde: 7000000,
        source: "Recharge interne",
        status: "Succès",
        date: 1672531200,
        client: "Tech Innovation",
        invoiceNumber: "12UP-2652HHH-12",
        note:
          "Aperçu du virement bancaire et informations supplémentaires liées à cette transaction.",
        auteur: {
          nom: "Maurice ALLOUGBA",
          fonction: "Manager Optimise",
          avatar: "avatar.png",
        },
      },
      {
        id: 2,
        amount: 500000,
        type: "Débit - abonnement mensuel",
        solde: 6500000,
        source: "Recharge interne",
        status: "Echec",
        date: 1672531200,
        client: "Innovation",
        invoiceNumber: "12UP-2652HH-13",
        auteur: {
          nom: "Jean KOUASSI",
          fonction: "Agent financier",
          avatar: "avatar.png",
        },
      },
      {
        id: 3,
        amount: 900000,
        type: "Débit - abonnement mensuel",
        solde: 6500000,
        source: "Recharge interne",
        status: "Echec",
        date: 1672531200,
        client: "Tech Innovation",
        invoiceNumber: "12UP-2652HH-14",
        auteur: {
          nom: "Jean KOUASSI",
          fonction: "Agent financier",
          avatar: "avatar.png",
        },
      },
    ],
    []
  )

  const table = useDataTable({
    data: transactionsData,
    columns,
    initialColumnPinning: { right: ["actions"] },
  })


  return (
    <>
      <div className=" cards bg-white rounded-xl shadow-sm">
        <DataTable
          table={table}
          noDataMessage="Aucune transaction"
          tableLayout="fixed"
        >
          <DataTableToolbar table={table} />
        </DataTable>
      </div>

      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="!max-w-xl ">
          <SheetHeader>
            <SheetTitle>Détails de la transaction</SheetTitle>
            <SheetDescription>
              {selectedTransaction && (
                <div className="space-y-6 pt-6">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">Date de la transaction</p>
                      <p className="text-black font-semibold">{getLocalStringDate(selectedTransaction.date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">État de la transaction</p>
                      <span className={cn("text-black px-3 py-1 rounded-lg text-xs font-medium", getStatusStyle(selectedTransaction.status))}>
                        {selectedTransaction.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">ID de la transaction</p>
                      <p className="text-black font-semibold">{selectedTransaction.invoiceNumber}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">Type de transaction</p>
                      <p className=" font-semibold text-black">{selectedTransaction.type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">Auteur de la transaction</p>
                      <div className="flex  space-x-2 text-black font-semibold items-center">
                        {selectedTransaction.auteur?.avatar && (
                          <img
                            src={getFilePath(selectedTransaction.auteur.avatar)}
                            className="w-10 h-10 rounded-full"
                            alt={selectedTransaction.auteur.nom}
                          />
                        )}
                        <div>
                          <p className="font-semibold text-[#173E48]">{selectedTransaction.auteur?.nom}</p>
                          <p className="text-xs text-[#225C6B]">{selectedTransaction.auteur?.fonction}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6 items-center">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2">
                      <p className="text-sm text-purple-700">Virement bancaire</p>
                    </div>
                    <div className="flex flex-col ml-6">
                      <p className="text-sm text-[#225C6B] mb-1">Client</p>
                      <p className="font-semibold text-black">{selectedTransaction.client}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#225C6B] mb-1">Montant</p>
                      <p className="text- sm font-bold text-black">{formatMoney(selectedTransaction.amount)} F cfa</p>
                    </div>
                  </div>
                  <div className=" rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                          <img src={getFilePath("pdf.svg")} alt="pdf" />

                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Fichier justificatif virement</p>
                        </div>
                      </div>
                      <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
                        Télécharger le justificatif
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-[#225C6B] mb-2">Note</p>
                    <p className="text-sm text-gray-600">{selectedTransaction.note ?? "Aucune note disponible"}</p>
                  </div>
                </div>
              )}
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default TransactionDataTable